import { sanitizePII } from '@/lib/analytics/sanitize';

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

/**
 * Finds the error in a log call: a direct Error argument, or the `err`/`error`
 * field of a context object (`slog.error({ err }, 'msg')`), or any Error value.
 */
function findLoggedError(args: unknown[]): unknown {
  for (const arg of args) {
    if (arg instanceof Error) return arg;
  }

  for (const arg of args) {
    if (!isRecord(arg)) continue;
    if (arg.err !== undefined) return arg.err;
    if (arg.error !== undefined) return arg.error;

    const nested = Object.values(arg).find((value) => value instanceof Error);
    if (nested) return nested;
  }

  return undefined;
}

export type LoggedError = {
  error: unknown;
  /** The log's message with emails, IDs and keys stripped (`sanitizePII`). */
  logMessage?: string;
  userId?: string;
};

/**
 * Turns error-level log arguments into something reportable. A log without an
 * error object is reported under its message, so `log.error('Sync failed')`
 * still reaches error tracking.
 */
export function getLoggedError(
  args: unknown[],
  fallbackMessage: string,
): LoggedError {
  const logMessage = sanitizePII(
    args.find((arg): arg is string => typeof arg === 'string'),
  );
  const userId = args
    .filter(isRecord)
    .map((arg) => arg.userId)
    .find((value): value is string => typeof value === 'string');

  return {
    // The fallback error's message is what error tracking groups and shows,
    // so it is the sanitized text, never the raw log line.
    error: findLoggedError(args) ?? new Error(logMessage ?? fallbackMessage),
    logMessage,
    userId,
  };
}
